import { Document, Font, Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import { useMemo } from "react";

import type { Responsible, Talent } from "@/api/talent/models";
import { CLAPPERBOARD_COLORS } from "@/constants/talentClapperboard";
import { ClothingSizeEnum, ShoeSizeEnum } from "@/enums/measurements";
import {
  extractFirstAndLastName,
  formatCentimetersToMeters,
  formatDateTimeWithHour,
  isoDateToStandardDate,
} from "@/utils";
import { verifyIfTalentIsAdult } from "@/utils/features/talent/validations";
import { ClapperboardDataCell as DataCell } from "../ClapperboardDataCell";

type Props = Readonly<{
  talent: Talent;
  responsible: Responsible | null;
  generatedAt: Date;
}>;

Font.register({
  family: "Gotham",
  fonts: [
    { src: "/fonts/gotham/gotham-book.otf", fontWeight: 400 },
    { src: "/fonts/gotham/gotham-bold.otf", fontWeight: 700 },
  ],
});

const styles = StyleSheet.create({
  page: {
    fontFamily: "Gotham",
    backgroundColor: CLAPPERBOARD_COLORS.white,
    color: CLAPPERBOARD_COLORS.text,
    padding: 80 / 2,
    justifyContent: "center",
    gap: 40 / 2,
  },
  header: {
    flexDirection: "column",
    alignItems: "center",
  },
  talentName: {
    fontSize: 80 / 2,
    fontWeight: 700,
    textAlign: "center",
    maxLines: 1,
    textOverflow: "ellipsis",
  },
  talentCode: {
    fontSize: 48 / 2,
    fontWeight: 700,
    textAlign: "center",
    marginTop: 4,
  },
  section: {
    gap: 40 / 2,
  },
  row: {
    flexDirection: "row",
    gap: 40 / 2,
  },
  sectionTitle: {
    fontSize: 48 / 2,
    fontWeight: 400,
    textAlign: "center",
  },
  footer: {
    fontSize: 26 / 2,
    fontWeight: 700,
    textAlign: "right",
    position: "absolute",
    bottom: 80 / 2,
    right: 80 / 2,
  },
});

function dashed(keys: string[] | null | undefined, enumMap: Record<string, string>): string {
  if (!keys?.length) {
    return "";
  }

  return keys.map((key) => enumMap[key] ?? key).join(" - ");
}

export const ClapperboardPDFDocument = ({ talent, responsible, generatedAt }: Props) => {
  const isTalentAdult = verifyIfTalentIsAdult(talent.birthday);

  const entries = useMemo(() => {
    const attributes = talent.attribute;

    return {
      responsible: isTalentAdult ? talent : responsible,
      birthday: isoDateToStandardDate(talent.birthday),
      height: attributes?.height != null ? formatCentimetersToMeters(attributes.height) : "",
      clothingSize: dashed(attributes?.clothingSize, ClothingSizeEnum),
      shoeSize: dashed(attributes?.shoeSize, ShoeSizeEnum),
      generatedAt: formatDateTimeWithHour(generatedAt),
    };
  }, [talent, responsible, generatedAt, isTalentAdult]);

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.page}>
        {/* Cabeçalho */}
        <View style={styles.header}>
          <Text style={styles.talentName}>{extractFirstAndLastName(talent.fullName)}</Text>
          <Text style={styles.talentCode}>{talent.talentCode}</Text>
        </View>

        {/* Seção 1 */}
        {/* Linha 1 */}
        <View style={styles.section}>
          <View style={styles.row}>
            <DataCell label="Nascimento" value={entries.birthday} />
            <DataCell label="Altura" value={entries.height} />
            <DataCell label="Manequim" value={entries.clothingSize} />
            <DataCell label="Sapato" value={entries.shoeSize} />
            <DataCell label="Peso" value="" />
          </View>

          {/* Linha 2 */}
          <View style={styles.row}>
            <DataCell label="Material" flex={1} />
            <DataCell label="Makehair" flex={2.25} />
            <DataCell label="Fotógrafo" flex={2.25} />
          </View>
        </View>

        {/* Seção responsável */}
        <View style={styles.section}>
          {!isTalentAdult && <Text style={{ ...styles.sectionTitle, marginTop: 40 / 2 }}>Dados do responsável</Text>}

          {/* Linha 3 */}
          <View style={styles.row}>
            {!isTalentAdult && (
              <DataCell label="Nome" value={extractFirstAndLastName(entries.responsible?.fullName ?? "")} />
            )}

            <DataCell label="Telefone" value={entries.responsible?.phone} />
          </View>

          {/* Linha 4 */}
          <View style={styles.row}>
            <DataCell label="E-mail" value={entries.responsible?.email} />
          </View>
        </View>

        {/* Rodapé */}
        <Text style={styles.footer}>{entries.generatedAt}</Text>
      </Page>
    </Document>
  );
};
